import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, eur, errMsg, useSettings } from "@/lib/api";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none focus:border-[#0066FF]";
const FIELDS = [["name", "Nome e cognome", "sm:col-span-2"], ["email", "Email"], ["phone", "Telefono"], ["address", "Indirizzo e numero civico", "sm:col-span-2"], ["zip", "CAP"], ["city", "Città"], ["province", "Provincia"]];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const settings = useSettings();
  const nav = useNavigate();
  const [c, setC] = useState({ name: "", email: "", phone: "", address: "", zip: "", city: "", province: "", notes: "" });
  const [method, setMethod] = useState("");
  const [coupon, setCoupon] = useState({ code: "", applied: null });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [admin, setAdmin] = useState(null);
  const methods = settings?.shipping?.methods || [];

  useEffect(() => {
    api.get("/auth/me").then(() => setAdmin(true)).catch(() => setAdmin(false));
  }, []);
  useEffect(() => {
    if (!method && methods[0]) setMethod(methods[0].id);
  }, [settings, method]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = methods.find((x) => x.id === method);
  const thr = settings?.shipping?.free_threshold;
  const shipping = !m || items.every((i) => i.free_shipping) || (thr && subtotal >= thr) ? 0 : m.price;
  const discount = coupon.applied?.amount || 0;
  const total = useMemo(() => Math.max(0, subtotal + shipping - discount), [subtotal, shipping, discount]);
  const canPay = settings?.payments_enabled || admin;

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/check", { code: coupon.code, total: subtotal + shipping });
      setCoupon({ code: data.code, applied: data });
      toast.success(`Coupon applicato: -${eur(data.amount)}`);
    } catch (e) {
      setCoupon({ ...coupon, applied: null });
      toast.error(errMsg(e));
    }
  };
  const pay = async (e) => {
    e.preventDefault();
    const payMethod = e.nativeEvent?.submitter?.value === "klarna" ? "klarna" : "card";
    setBusy(payMethod);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, options: i.options.map(({ name, value }) => ({ name, value })), qty: i.qty })),
        customer: c, shipping_method: method, coupon_code: coupon.applied?.code || null, origin_url: window.location.origin, accept_terms: terms, pay_method: payMethod,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!items.length) nav("/carrello");
  }, [items, nav]);
  if (!items.length) return null;
  return (
    <form onSubmit={pay} className="mx-auto max-w-7xl px-4 pt-10 sm:px-6" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-bold tracking-[-0.03em] text-[#0A0B0E] sm:text-4xl">Checkout</h1>
      {settings && !settings.payments_enabled && admin !== null && (
        <p className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="checkout-disabled-notice">
          {admin ? "Modalità test amministratore: pagamenti di prova Stripe (carta 4242 4242 4242 4242). Nessun addebito reale." : "I pagamenti online non sono ancora attivi: al momento non è possibile inviare ordini."}
        </p>
      )}
      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_400px]">
        <div className="space-y-8">
          <section>
            <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Dati di consegna</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map(([k, l, cls]) => (
                <input key={k} required type={k === "email" ? "email" : "text"} placeholder={`${l} *`} value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} className={`${field} ${cls || ""}`} data-testid={`checkout-${k}`} />
              ))}
              <textarea rows={2} placeholder="Note per la consegna" value={c.notes} onChange={(e) => setC({ ...c, notes: e.target.value })} className={`${field} h-auto py-3 sm:col-span-2`} data-testid="checkout-notes" />
            </div>
            <p className="mt-2 text-xs text-slate-500">Spedizioni previste in Italia.</p>
          </section>
          <section>
            <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Spedizione</h2>
            <div className="space-y-2">
              {methods.map((x) => (
                <label key={x.id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm text-slate-900 ${method === x.id ? "border-[#0066FF]" : "border-slate-200"}`}>
                  <span className="flex items-center gap-3"><input type="radio" checked={method === x.id} onChange={() => setMethod(x.id)} className="accent-[#0066FF]" data-testid={`checkout-shipping-${x.id}`} />{x.name}{x.days && <span className="text-xs text-slate-500">{x.days}</span>}</span>
                  <span className="font-mono text-xs">{eur(items.every((i) => i.free_shipping) ? 0 : x.price)}</span>
                </label>
              ))}
            </div>
          </section>
        </div>
        <aside className="h-fit space-y-5 rounded-3xl border border-slate-200 bg-[#F5F7FA] p-6 text-slate-900 lg:sticky lg:top-36">
          <ul className="space-y-2 text-sm">
            {items.map((i) => <li key={i.key} className="flex justify-between gap-3"><span className="min-w-0">{i.name} × {i.qty}</span><span className="shrink-0">{eur(i.unit_price * i.qty)}</span></li>)}
          </ul>
          <div className="flex gap-2">
            <input value={coupon.code} onChange={(e) => setCoupon({ code: e.target.value.toUpperCase(), applied: null })} placeholder="Codice coupon" className={field} data-testid="checkout-coupon-input" />
            <button type="button" onClick={applyCoupon} disabled={!coupon.code} className="rounded-xl bg-[#0A0B0E] px-4 text-sm font-medium text-white disabled:opacity-40" data-testid="checkout-coupon-apply">Applica</button>
          </div>
          <dl className="space-y-2 border-t border-slate-200 pt-4 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Subtotale</dt><dd>{eur(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Spedizione</dt><dd data-testid="checkout-shipping-cost">{eur(shipping)}</dd></div>
            {discount > 0 && <div className="flex justify-between text-[#0066FF]"><dt>Coupon {coupon.applied.code}</dt><dd>-{eur(discount)}</dd></div>}
            <div className="flex justify-between pt-2 font-display text-xl font-bold"><dt>Totale</dt><dd data-testid="checkout-total">{eur(total)}</dd></div>
          </dl>
          <label className="flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} required className="mt-0.5 accent-[#0066FF]" data-testid="checkout-terms" />
            <span>Ho letto e accetto i <Link to="/pagina/termini-e-condizioni" className="text-[#0066FF] underline">Termini e condizioni</Link> e la <Link to="/pagina/privacy" className="text-[#0066FF] underline">Privacy Policy</Link>. Sono informato del <Link to="/pagina/resi-e-rimborsi" className="text-[#0066FF] underline">diritto di recesso</Link> e che, in caso di recesso, i costi diretti di restituzione sono a mio carico.</span>
          </label>
          <div className="space-y-2.5" data-testid="checkout-payment-methods">
            <button value="card" disabled={!canPay || !!busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0066FF] font-semibold text-white transition-colors hover:bg-[#1a75ff] disabled:cursor-not-allowed disabled:bg-slate-300" data-testid="checkout-pay-btn">
              <Lock className="h-4 w-4" />{busy === "card" ? "Reindirizzamento…" : "Paga con carta"}
            </button>
            <button value="klarna" disabled={!canPay || !!busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FFB3C7] font-semibold text-[#0A0B0E] transition-colors hover:bg-[#ffa3bb] disabled:cursor-not-allowed disabled:bg-slate-300" data-testid="checkout-klarna-btn">
              {busy === "klarna" ? "Reindirizzamento…" : <>Paga in 3 rate con <span className="font-extrabold">Klarna.</span></>}
            </button>
          </div>
          <p className="text-xs text-slate-500">Ordine con obbligo di pagamento. Pagamento anticipato sicuro con Stripe: carte, Apple Pay e Google Pay. Klarna per gli acquisti idonei, soggetto ad approvazione di Klarna.</p>
        </aside>
      </div>
    </form>
  );
}
